import { Popover, UnstyledButton } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { useSwitchCompany } from "@/lib/queries/useCompanyOnboarding";
import { useMyWorkspaces } from "@/lib/queries/useWorkspaces";
import { useCompanyStore } from "@/store/company-store";
import { useWorkspaceStore } from "@/store/workspace-store";

/** 회사·워크스페이스 이니셜 배지 문자(첫 글자). */
function badge(label: string): string {
  return label.trim().slice(0, 1) || "?";
}

/**
 * 워크스페이스 전환 스위처 (사이드바 상단).
 *
 * 회사당 단일 워크스페이스 정책에 따라 평면 목록으로 표시함.
 * UI에서 회사 차원을 노출하지 않고 워크스페이스 단위로만 전환함.
 * 다회사 사용자의 경우 내부적으로 회사 전환(switchCompany)을 처리하되 UI에는 드러내지 않음.
 */
export function OrgSwitcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspace: currentSlug } = useParams<{ workspace: string }>();

  const activeCompanyId = useCompanyStore((s) => s.activeCompanyId);
  const switchCompany = useSwitchCompany();

  const currentWs = useWorkspaceStore((s) => s.workspace);
  const [opened, setOpened] = useState(false);

  const { data: workspaces, isFetching } = useMyWorkspaces();

  const wsLabel = currentWs?.name ?? "";

  const close = () => setOpened(false);

  const onSelectWorkspace = (slug: string, companyId?: string) => {
    close();
    if (companyId && companyId !== activeCompanyId) {
      switchCompany(companyId, slug);
    } else if (slug !== currentSlug) {
      navigate(`/${slug}/dashboard`);
    }
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-start"
      width={220}
      shadow="md"
      radius="md"
      withinPortal
    >
      <Popover.Target>
        <UnstyledButton className="org-switcher-btn" onClick={() => setOpened((o) => !o)}>
          <span className="org-ico">{badge(wsLabel)}</span>
          <span className="org-txt">
            <span className="org-ws">{wsLabel}</span>
          </span>
          <svg
            className="org-chev"
            viewBox="0 0 24 24"
            width={16}
            height={16}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            style={{ transform: opened ? "rotate(180deg)" : "none", transition: "transform .15s" }}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </UnstyledButton>
      </Popover.Target>

      <Popover.Dropdown p={0}>
        <div className="org-dd">
          <div className="org-col">
            <div className="org-col-label">{t("nav.workspace", "워크스페이스")}</div>
            {isFetching && (workspaces ?? []).length === 0 ? (
              <div className="org-empty">{t("common.loading", "불러오는 중…")}</div>
            ) : (workspaces ?? []).length === 0 ? (
              <div className="org-empty">{t("common.noData", "데이터 없음")}</div>
            ) : (
              (workspaces ?? []).map((w) => {
                const isCurrent = w.slug === currentSlug;
                return (
                  <button
                    key={w.slug}
                    type="button"
                    className={`org-item${isCurrent ? " sel" : ""}`}
                    onClick={() => onSelectWorkspace(w.slug, w.companyId)}
                  >
                    <span className="org-dot">{badge(w.name)}</span>
                    <span className="org-name">{w.name}</span>
                    {isCurrent && <span className="org-check">✓</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}
